<script lang="ts">
  import { inject, provide, ref, type InjectionKey } from 'vue'
  import {
    type AgencySettingName,
    type SettingName,
    type GlobalSettingName
  } from '@gat-ship/types'
  import type { GlobalSettingsSnapshot } from '../model/settings-lookup'
  import { useSettingsLookup } from '../composables/useSettingsLookup'

  export function createGlobalSettingsContext() {
    const {
      lookupAgencySetting,
      lookupGlobalSetting,
      resolveSetting,
      decideInitialSnapshot
    } = useSettingsLookup()

    const _allGlobalSettings = ref<GlobalSettingsSnapshot>({
      agencySettingsMap: {},
      settings: {}
    })

    async function _initialize(contexts: {
      userId: string
      databaseId?: string
    }) {
      const next = decideInitialSnapshot(contexts)
      if (next) {
        _allGlobalSettings.value = next
      }
    }

    function getAgencySetting(params: {
      setting: AgencySettingName
      agencyId: string
      defaultValue: boolean | number | string | undefined
    }): boolean | number | string | undefined {
      return lookupAgencySetting(_allGlobalSettings.value, params)
    }

    function getGlobalSetting(params: {
      setting: GlobalSettingName
      defaultValue: boolean | number | string | undefined
    }): boolean | number | string | undefined {
      return lookupGlobalSetting(_allGlobalSettings.value, params)
    }

    function getSetting(params: {
      setting: SettingName
      agencyId?: string
      defaultValue: boolean | number | string | undefined
    }): boolean | number | string | undefined {
      const { kind, value } = resolveSetting(_allGlobalSettings.value, params)
      if (kind === 'unknown') {
        console.warn(`Unknown setting: ${params.setting}`)
      }
      return value
    }

    return {
      _initialize,
      getGlobalSetting,
      getAgencySetting,
      getSetting
    }
  }

  export type GlobalSettingsContext = ReturnType<
    typeof createGlobalSettingsContext
  >

  export const globalSettingsKey = Symbol(
    'gs-global-settings'
  ) as InjectionKey<GlobalSettingsContext>

  export function useGlobalSettings(): Omit<
    GlobalSettingsContext,
    '_initialize' | 'isInitialized'
  > {
    const instance = inject(globalSettingsKey)

    if (!instance) {
      throw new Error('Global settings context not found')
    }

    // if (!instance.isInitialized.value) {
    //   instance._initialize({ userId: '', databaseId: '' })
    // }

    return instance
  }

  export function _useGlobalSettings(): GlobalSettingsContext {
    const instance = inject<GlobalSettingsContext>(globalSettingsKey)

    if (!instance) {
      throw new Error('Global settings context not found')
    }

    return instance
  }
</script>
<script setup lang="ts">
  const globalSettingsContext = createGlobalSettingsContext()
  provide(globalSettingsKey, globalSettingsContext)

  defineExpose(globalSettingsContext)
</script>
<template>
  <slot />
</template>
